import { useCallback, useMemo } from 'react';

import type {
  ApprovalAction,
  ApprovalType,
  ApprovalConfigData,
  ApprovalConfigEntity,
  ApprovalSkipStrategy,
  ApprovalTemplate,
} from 'ui-product-approvals-common';
import { normalizeApprovalTemplate } from 'ui-product-approvals-common/utils/normalizeApprovalTemplate';

import { captureException } from '../utils/sentryUtils';
import { useGetApprovalConfigurationQuery } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsConfigurationRpc.hooks';
import { useListApprovalSettingsAssetsQuery } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsMetadataRpc.hooks';
import { APPROVAL_TYPE_TO_OBJECT_TYPE_ID } from 'ui-product-approvals-common/constants/approvalTypeIds';

export const useApprovalConfig = (
  approvalType: ApprovalType,
  approvalAction: ApprovalAction
): ApprovalConfigEntity => {
  const {
    data: queryData,
    loading: queryLoading,
    error: queryError,
    refetch: queryRefetch,
  } = useGetApprovalConfigurationQuery({
    variables: {
      approvalTypeId: APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType],
      approvalAction,
    },
    // fetchPolicy 'network-only' ensures fresh data. If removing this need to test extensively
    fetchPolicy: 'network-only',
    onError: (error) => {
      captureException(error, {
        extra: {
          fingerprint: 'useApprovalConfig',
          approvalType,
          approvalAction,
        },
      });
    },
  });

  const {
    data: settingsAssetsData,
    loading: settingsAssetsLoading,
    error: settingsAssetsError,
    refetch: refetchSettingsAssets,
  } = useListApprovalSettingsAssetsQuery({
    fetchPolicy: 'network-only',
  });

  const approvalTemplatesEnabled = useMemo(
    () =>
      settingsAssetsData?.assets.some(
        (asset) =>
          asset.objectTypeId ===
            APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType] &&
          asset.approvalAction === approvalAction &&
          asset.approvalTemplatesEnabled === true
      ) ?? false,
    [approvalAction, approvalType, settingsAssetsData]
  );

  const config = useMemo((): ApprovalConfigData => {
    if (!queryData) {
      // If we cannot get a response from config endpoint, we will set approvals to enabled:false
      const enabled = queryError ? false : null;
      return { approvalAction, approvalType, enabled, portalId: null };
    }
    const { approvalConfiguration } = queryData;
    return {
      // We already have the approvalAction and approvalType so don't need to use the response values,
      // this also avoids the TS error from unhandled enum case. In future I want to remove all FE types tho
      approvalAction,
      approvalType,
      enabled: approvalConfiguration.enabled,
      portalId: approvalConfiguration.portalId,
      skipApprovalOption: approvalConfiguration.skipApprovalOption as
        | ApprovalSkipStrategy
        | undefined,
      usersIdsListThatCanSkipApproval:
        approvalConfiguration.usersIdsListThatCanSkipApproval,
      // The metadata RPC is the frontend proxy for effective per-asset FAS.
      // Only expose template payload when that capability is enabled.
      approvalTemplate: approvalTemplatesEnabled
        ? normalizeApprovalTemplate(
            approvalConfiguration.approvalTemplate as
              | ApprovalTemplate
              | undefined
          )
        : undefined,
    };
  }, [
    approvalAction,
    approvalTemplatesEnabled,
    approvalType,
    queryData,
    queryError,
  ]);

  const fetchConfig = useCallback(() => {
    void queryRefetch();
    void refetchSettingsAssets();
  }, [queryRefetch, refetchSettingsAssets]);

  return useMemo(
    () => ({
      config,
      fetchConfig,
      hasFetchError: Boolean(queryError || settingsAssetsError),
      isLoading: queryLoading || settingsAssetsLoading,
    }),
    [
      queryError,
      settingsAssetsError,
      queryLoading,
      settingsAssetsLoading,
      config,
      fetchConfig,
    ]
  );
};
